/**
 * **演出が置いたものの生存**（Issue #534。索引 #525。設計文書 §4）。**`duelEditor/` の外**。
 *
 * 演出（帯）がやるのは **置く（`places`）/ 消す（`clears`）** の**宣言**だけで、
 * **生存はここが持つ** —— 帯が終わっても消えず、**別の演出が消す**まで残る。
 *
 * ## なぜ宣言なのか
 *
 * 描画中に命令的に置くと**シークで壊れる**（同じ入力なら同じ絵、が破れて書き出しとプレビューがずれる）。
 * 整列後の帯を**時刻順に 1 回**畳んで「いつからいつまで出ているか」の区間にしておけば、
 * フレームごとは ms で絞るだけになる。
 *
 * ## なぜ `fold` ではなく `build` なのか
 *
 * `duelEditor/timeline/states.js` の `fold` は**アンカーの刻み**でしか steps を打てないが、
 * 置かれるのは**帯の途中**（シンクロがモンスターを出すのは帯の頭から 2.75 秒後）。
 * だから `build` チャンネルにして、整列後の帯（`objects`）を受け取る。
 */
import { checkPlacements, markId, markOf } from '../duelEditor/core/mark';

/** 演出のオブジェクトの状態チャンネルの id */
export const FX_OBJECTS = 'fx_objects';

const num = (v, def = 0) => (Number.isFinite(v) ? v : def);

/**
 * 帯の一覧 → **置かれたものの区間**。
 *
 * 置いた時刻は帯の頭（＋`atMs`）、消えるのは
 * **「それより後で最初に消した帯の時刻」か「置いた帯の終わり」**（`persist` を名乗らないものは帯と一緒に消える）。
 *
 * @param objects 整列後の帯 `[{ id, type, startMs, endMs, payload, hidden }]`
 * @param effectOf 帯の型の id → 演出
 * @param contextOf 帯 → `places` / `clears` に渡す context（盤面・履歴）
 * @returns `{ spans, issues }`
 */
export function buildFxObjects(objects = [], effectOf = () => null, contextOf = () => ({})) {
  /** 置く / 消すを時刻順に並べる */
  const events = [];
  const issues = [];
  (objects ?? []).forEach((clip) => {
    if (clip?.hidden) return;
    const effect = effectOf(clip.type);
    if (!effect) return;
    const ctx = { ...contextOf(clip), payload: clip.payload ?? {} };
    const at = num(clip.startMs);
    try {
      checkPlacements(effect.places?.(ctx), `effect ${effect.situation}: places`).forEach((p) => {
        // **`at` は先頭の階層でも受ける**（型の `Placement.at`。描く側は `data.at` を読むので寄せる。Issue #541）
        const data = p.at ? { ...(p.data ?? {}), at: p.at } : (p.data ?? null);
        events.push({ kind: 'place', ms: at + num(p.atMs), clip, mark: p.mark, key: p.key ?? null, data, endMs: num(clip.endMs, at) });
      });
      checkPlacements(effect.clears?.(ctx), `effect ${effect.situation}: clears`).forEach((c) => {
        events.push({ kind: 'clear', ms: at + num(c.atMs), clip, mark: c.mark, key: c.key ?? null, prefix: c.prefix ?? null });
      });
    } catch (e) {
      // **綴り間違いで画面を落とさない** —— 診断に出して、その帯のぶんだけ諦める
      issues.push({ code: 'fx_object_unknown_mark', level: 'error', message: String(e?.message ?? e) });
    }
  });
  events.sort((a, b) => a.ms - b.ms || (a.kind === 'clear' ? -1 : 1));

  /** 出ているもの（id → 区間） */
  const open = new Map();
  const spans = [];
  events.forEach((ev) => {
    if (ev.kind === 'place') {
      const id = markId(ev.mark, ev.key);
      // 同じ id を置き直したら前のを閉じる（上書き）
      if (open.has(id)) { const prev = open.get(id); prev.endMs = ev.ms; spans.push(prev); }
      open.set(id, { id, mark: ev.mark, key: ev.key, data: ev.data, startMs: ev.ms, endMs: ev.endMs, placedBy: ev.clip.type });
      return;
    }
    // 消す: key を省いたらその印を全部。`prefix` は key の頭が一致するものを全部（チェーンの番号で束ねた印。Issue #571）
    [...open.entries()].forEach(([id, span]) => {
      if (span.mark !== ev.mark) return;
      if (ev.key != null && span.key !== ev.key) return;
      if (ev.prefix != null && !String(span.key ?? '').startsWith(String(ev.prefix))) return;
      span.endMs = ev.ms;
      spans.push(span);
      open.delete(id);
    });
  });
  // **誰も消さなかったもの** —— 置いた帯の終わりで消える（`persist` を名乗るものは診断に出す）
  [...open.values()].forEach((span) => {
    spans.push(span);
    issues.push({
      code: 'fx_object_leak',
      level: 'warn',
      message: `${span.placedBy} が置いた ${span.mark} が最後まで消されていない（${span.id}）`,
    });
  });
  spans.sort((a, b) => a.startMs - b.startMs);
  return { spans: Object.freeze(spans), issues: Object.freeze(issues) };
}

/** そのフレームに出ているもの */
export function fxObjectsAt(frameMs, built) {
  const ms = num(frameMs);
  return (built?.spans ?? []).filter((s) => s.startMs <= ms && (s.endMs == null || ms < s.endMs));
}

/**
 * 状態チャンネルにする（`duelEditor/timeline/states.js` の `build` 型）。
 *
 * **`build(anchors, ctx, objects)`** —— 第 3 引数の整列後の帯が要るのでこの形
 * （`states.js` に 1 行足して渡すようにした。Issue #534）。
 */
export function fxObjectsChannel({ effectOf, contextOf }) {
  return {
    id: FX_OBJECTS,
    label: '演出が置いたもの',
    kind: 'build',
    // context は帯ごとに引く（`anchors` と `ctx` から盤面・履歴を組む。Issue #541）
    build: (anchors, ctx, objects) => buildFxObjects(objects, effectOf, (clip) => (contextOf ? contextOf(clip, { anchors, ctx }) : {})),
    at: (ms, built) => fxObjectsAt(ms, built),
  };
}

export { markOf };
